package org.maplibre.compose.demoapp

import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.UiComposable
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.demoapp.demos.CastelloPlanDemo
import org.maplibre.compose.demoapp.demos.DataVizDemo
import org.maplibre.compose.demoapp.demos.LiveTrackingDemo
import org.maplibre.compose.demoapp.demos.MagnifyingLensDemo
import org.maplibre.compose.demoapp.demos.Manhattan3dDemo
import org.maplibre.compose.demoapp.demos.MapControlsDemo
import org.maplibre.compose.demoapp.demos.MapSnapshotterDemo
import org.maplibre.compose.demoapp.demos.MaterialStyleDemo
import org.maplibre.compose.demoapp.demos.TransitNetworkDemo
import org.maplibre.compose.demoapp.demos.editablemarkers.EditableMarkersDemo
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MapUiOptions
import org.maplibre.compose.overlay.MapOverlayScope
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.spatialk.geojson.BoundingBox
import org.maplibre.spatialk.geojson.Position

/**
 * Selecting a demo composes [MapContent] into the shared map and [Overlay] on top of the map. The
 * camera moves to [destination]. Each demo owns its state internally.
 */
interface Demo {
  val name: String
  val description: String
  val destination: DemoDestination

  /**
   * Replaces the user's chosen light style while this demo is selected. See [DemoStyle].
   *
   * Override only when the demo requires the style's data, fonts, or colors. Otherwise, use the
   * user's chosen style.
   */
  val preferredLightStyle: DemoStyle?
    get() = null

  /** The preferred dark style. Defaults to the light style for demos with a fixed basemap. */
  val preferredDarkStyle: DemoStyle?
    get() = preferredLightStyle

  /** An optional map pin that restores a useful view of the demo. */
  val pointerPin: DemoPointerPin?
    get() = null

  /** Camera controls and app interactions while this demo is selected. */
  fun interactions(mapState: MapState): MapInteractions = MapInteractions.Standard

  /** Input and presentation modifiers applied to the shared map while this demo is selected. */
  @UiComposable @Composable fun mapModifier(mapState: MapState): Modifier = Modifier

  /** Compose UI input bindings while this demo is selected, edited from the app [settings]. */
  fun uiOptions(settings: MapUiOptions): MapUiOptions = settings

  /** Map layers using the active [style], including its label font stack. */
  @MaplibreComposable @Composable fun MapContent(style: DemoStyle) {}

  /**
   * Compose UI drawn over the map while this demo is selected. [state] exposes the shell's
   * settings, style, and camera.
   *
   * [org.maplibre.compose.overlay.MapOverlayScope.placedAt] pins a child to a geographic position.
   */
  @UiComposable @Composable fun MapOverlayScope.Overlay(state: DemoAppState) {}

  /**
   * Controls shown in the sheet or side panel while this demo is selected. [state] exposes the
   * shell's settings, style, and camera.
   */
  @UiComposable @Composable fun Panel(state: DemoAppState) {}
}

/** The camera movement that occurs when a demo is selected or its pointer pin is pressed. */
sealed interface DemoDestination {
  /** Fits a geographic region inside the camera viewport. */
  data class FitBounds(val bounds: BoundingBox) : DemoDestination

  /** Moves to a complete camera position without deriving a zoom level from geographic bounds. */
  data class ExactCamera(val position: CameraPosition) : DemoDestination

  /** Preserves the current camera until the demo moves it from live data or user input. */
  data object None : DemoDestination
}

/** A point shown on the map and the camera movement that its button restores. */
data class DemoPointerPin(val target: Position, val destination: DemoDestination)

internal val BoundingBox.center: Position
  get() {
    val centerLongitude = (west + if (east < west) east + 360.0 else east) / 2
    return Position(
      longitude = if (centerLongitude > 180.0) centerLongitude - 360.0 else centerLongitude,
      latitude = (south + north) / 2,
    )
  }

/** Demos appear in the shell in this order. */
val allDemos: List<Demo> =
  listOf(
    Manhattan3dDemo,
    CastelloPlanDemo,
    DataVizDemo,
    MapControlsDemo,
    LiveTrackingDemo,
    EditableMarkersDemo,
    MagnifyingLensDemo,
    MapSnapshotterDemo,
    TransitNetworkDemo,
    MaterialStyleDemo,
  )
